/**
 * Trial Discount Protection System
 *
 * Multilayer defense to ensure 40% trial discount is only applied once per customer.
 * Layers:
 * 1. Cookie - trial_purchased=1
 * 2. localStorage - ts_trial_fp (fingerprint hash)
 * 3. Customer metafield - trial.purchased_at (via Liquid)
 * 4. ThumbmarkJS - device fingerprint (sent to server)
 * 5. Address hash - server-side Redis (webhook)
 */

(function() {
  'use strict';

  // Detect environment from hostname
  // staging.tomandsawyer.com -> staging endpoint
  // www.tomandsawyer.com / tomandsawyer.com -> production endpoint
  function detectEligibilityApiUrl() {
    var hostname = window.location.hostname.toLowerCase();
    var baseUrl = 'https://northamerica-northeast2-tomsawyer-trial-reorder-catch.cloudfunctions.net';

    // Staging detection: staging subdomain OR -dev in Shopify domain
    if (hostname.indexOf('staging.') === 0 || hostname.indexOf('-dev.') !== -1) {
      return baseUrl + '/staging-check-trial-eligibility';
    }

    // Default to production
    return baseUrl + '/production-check-trial-eligibility';
  }

  // Configuration
  var CONFIG = {
    // USD: No discounts available (GETFRESH40/SAVE25-TRIAL-2 removed)
    discountCodeUSD: null,
    // CAD: Handle-based discount codes (matched via contains)
    discountCodesCAD: [
      { pattern: 'puppy', code: 'PUPPY-SAVE' },
      { pattern: 'novel-protein', code: 'NOVEL-PROTEIN-SAVE' },
      { pattern: 'protein', code: 'NOVEL-PROTEIN-SAVE' },
      { pattern: 'low-fat', code: 'LOWFAT-SAVE' },
      { pattern: 'picky', code: 'PICKY-EATER-SAVE' },
      { pattern: 'bestseller', code: 'BESTSELLERS-SAVE' },
      { pattern: 'cat', code: 'CAT TRIAL - SAVE30' }
    ],
    cookieName: 'trial_purchased',
    cookieTTL: 31536000, // 1 year in seconds
    localStorageKey: 'ts_trial_fp',
    localStorageDateKey: 'ts_trial_date',
    cartAttributeFingerprint: '_dfp',
    // Eligibility check API endpoint - auto-detected from hostname
    // Override with window.__trialEligibilityApiUrl if needed
    eligibilityApiUrl: window.__trialEligibilityApiUrl || detectEligibilityApiUrl()
  };

  // State
  var fingerprint = null;
  var serverEligibilityChecked = false;
  var serverEligibilityResult = null; // null = not checked, true = eligible, false = ineligible
  var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active ? window.Shopify.currency.active : 'CAD';

  /**
   * Get the appropriate discount code based on currency and product handle
   * @param {string} handle - Product handle (optional, required for CAD)
   * @returns {string|null} Discount code or null if not found
   */
  function getDiscountCode(handle) {
    if (currency === 'USD') {
      return CONFIG.discountCodeUSD;
    }
    // CAD: Match by handle pattern
    if (handle) {
      for (var i = 0; i < CONFIG.discountCodesCAD.length; i++) {
        if (handle.indexOf(CONFIG.discountCodesCAD[i].pattern) !== -1) {
          return CONFIG.discountCodesCAD[i].code;
        }
      }
    }
    return null;
  }

  /**
   * Get product handle from trial wrapper element
   */
  function getProductHandle(wrapper) {
    if (!wrapper) return null;
    return wrapper.getAttribute('data-product-handle');
  }

  /**
   * Fallback fingerprint using basic browser signals
   */
  function getFallbackFingerprint() {
    var signals = [
      navigator.userAgent,
      navigator.language,
      navigator.hardwareConcurrency || 0,
      navigator.deviceMemory || 0,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      new Date().getTimezoneOffset()
    ];

    // Canvas fingerprint
    try {
      var canvas = document.createElement('canvas');
      var ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('fingerprint', 2, 2);
      signals.push(canvas.toDataURL().slice(-50));
    } catch (e) {
      signals.push('canvas-error');
    }

    // Simple hash
    var data = signals.join('|');
    var hash = 0;
    for (var i = 0; i < data.length; i++) {
      var char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return 'fb_' + Math.abs(hash).toString(16);
  }

  /**
   * Get device fingerprint (ThumbmarkJS or fallback)
   */
  function getFingerprint() {
    if (fingerprint) {
      return Promise.resolve(fingerprint);
    }

    // Use ThumbmarkJS if available (loaded as separate script)
    if (window.ThumbmarkJS && window.ThumbmarkJS.getFingerprint) {
      return window.ThumbmarkJS.getFingerprint().then(function(fp) {
        fingerprint = fp;
        return fp;
      }).catch(function(err) {
        fingerprint = getFallbackFingerprint();
        return fingerprint;
      });
    }

    // Fallback if ThumbmarkJS not loaded
    fingerprint = getFallbackFingerprint();
    return Promise.resolve(fingerprint);
  }

  /**
   * Check server-side eligibility via API
   * This checks fingerprint and IP against Redis, and customer metafield if logged in
   * Returns Promise<{eligible: boolean, reason?: string}>
   */
  function checkServerEligibility() {
    // Return cached result if available
    if (serverEligibilityChecked) {
      return Promise.resolve({
        eligible: serverEligibilityResult !== false,
        reason: serverEligibilityResult === false ? 'server_check' : undefined,
        cached: true
      });
    }

    return getFingerprint().then(function(fp) {
      // Build request with fingerprint in header
      // IP is extracted server-side from request headers
      var customerId = window.__shopifyCustomerId || null;

      return fetch(CONFIG.eligibilityApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Fingerprint': fp || ''
        },
        body: JSON.stringify({
          fingerprint: fp,
          customerId: customerId
        })
      });
    }).then(function(response) {
      return response.json();
    }).then(function(data) {
      serverEligibilityChecked = true;
      serverEligibilityResult = data.eligible;
      return data;
    }).catch(function(err) {
      // On error, default to INELIGIBLE (fail-closed)
      // Better to miss a discount than cancel an order post-checkout
      // Legitimate customers can retry or contact support
      serverEligibilityChecked = true;
      serverEligibilityResult = false;
      return { eligible: false, error: err.message };
    });
  }

  /**
   * Check if server says ineligible (sync check of cached result)
   */
  function hasServerIneligibility() {
    return serverEligibilityChecked && serverEligibilityResult === false;
  }

  /**
   * Check cookie for trial purchase
   */
  function hasCookie() {
    return document.cookie.indexOf(CONFIG.cookieName + '=1') !== -1;
  }

  /**
   * Check localStorage for trial purchase
   */
  function hasLocalStorage() {
    try {
      return !!localStorage.getItem(CONFIG.localStorageKey);
    } catch (e) {
      return false;
    }
  }

  /**
   * Check customer metafield (set via Liquid)
   */
  function hasCustomerMetafield() {
    return !!window.__customerTrialPurchased;
  }

  /**
   * Check for QA override via URL parameter
   * Usage: ?trial=eligible (first-time) or ?trial=ineligible (returning)
   * SECURITY: Only works for logged-in customers with Staff/QA tag
   */
  function getQAOverride() {
    // Security check: only allow staff/QA customers to use override
    if (!window.__isStaffCustomer) {
      return null;
    }

    var params = new URLSearchParams(window.location.search);
    var override = params.get('trial');
    if (override === 'eligible') return 'eligible';
    if (override === 'ineligible') return 'ineligible';
    return null;
  }

  /**
   * Check all client-side layers + cached server result
   */
  function hasTrialHistory() {
    // QA override takes precedence
    var qaOverride = getQAOverride();
    if (qaOverride === 'eligible') {
      return false;
    }
    if (qaOverride === 'ineligible') {
      return true;
    }

    // Check client-side layers first (instant)
    if (hasCookie() || hasLocalStorage() || hasCustomerMetafield()) {
      return true;
    }

    // Check cached server result (if available)
    if (hasServerIneligibility()) {
      return true;
    }

    return false;
  }

  /**
   * Set cookie
   */
  function setCookie() {
    document.cookie = CONFIG.cookieName + '=1; max-age=' + CONFIG.cookieTTL + '; path=/; SameSite=Lax';
  }

  /**
   * Set localStorage
   */
  function setLocalStorage(fp) {
    try {
      localStorage.setItem(CONFIG.localStorageKey, fp || 'unknown');
      localStorage.setItem(CONFIG.localStorageDateKey, new Date().toISOString());
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Store fingerprint in cart attributes
   */
  function storeFingerprint(fp) {
    var attributes = {};
    attributes[CONFIG.cartAttributeFingerprint] = fp;

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function(response) {
      return response.json();
    }).catch(function(err) {
      // Non-critical: fingerprint storage failed
    });
  }

  /**
   * Add product to cart via XMLHttpRequest
   * Bypasses fetch interceptors from cart-manager.js and analytics-tracker.js
   */
  function addToCart(variantId, quantity) {
    if (!variantId) {
      return Promise.reject(new Error('No variant ID provided'));
    }

    return new Promise(function(resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', '/cart/add.js', true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');

      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          try {
            var data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(data);
            } else {
              reject(new Error(data.description || data.message || 'Failed to add to cart'));
            }
          } catch (e) {
            reject(e);
          }
        }
      };

      xhr.onerror = function() {
        reject(new Error('Network error'));
      };

      xhr.send(JSON.stringify({
        id: parseInt(variantId, 10),
        quantity: quantity || 1
      }));
    });
  }

  /**
   * Apply discount code to session via /discount/{code} endpoint
   * This sets a cookie that persists the discount to checkout
   */
  function applyDiscountCode(code) {
    if (!code) {
      return Promise.resolve(false);
    }

    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin'
    }).then(function(response) {
      return response.ok;
    }).catch(function(err) {
      return false;
    });
  }

  /**
   * Global click handler to intercept Shopify payment buttons through Shadow DOM
   * Uses composedPath() to see through Shadow DOM boundaries
   */
  var isApplyingDiscount = false;

  function setupGlobalPaymentButtonInterceptor() {
    document.addEventListener('click', function(e) {
      // Skip if we're in the middle of re-triggering after discount apply
      if (isApplyingDiscount) return;

      // Use composedPath to see through Shadow DOM
      var path = e.composedPath ? e.composedPath() : [e.target];

      // Find if click was on a Shopify payment button
      var paymentButton = null;
      var trialWrapper = null;

      for (var i = 0; i < path.length; i++) {
        var el = path[i];
        if (!el.classList) continue;

        // Check for payment button (may be inside Shadow DOM)
        if (!paymentButton && el.classList.contains('shopify-payment-button__button')) {
          paymentButton = el;
        }

        // Check for trial wrapper (outside Shadow DOM)
        if (!trialWrapper && el.hasAttribute && el.hasAttribute('data-trial-wrapper')) {
          trialWrapper = el;
        }
      }

      // Not a payment button click inside a trial wrapper
      if (!paymentButton || !trialWrapper) return;

      var productHandle = trialWrapper.getAttribute('data-product-handle');
      var isFirstTime = !hasTrialHistory();

      // Store fingerprint (non-blocking)
      getFingerprint().then(function(fp) {
        storeFingerprint(fp).catch(function() {});
      });

      // For first-time customers, apply the correct discount for THIS product
      if (isFirstTime && productHandle) {
        var discountCode = getDiscountCode(productHandle);
        if (discountCode) {
          // Prevent the checkout
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          applyDiscountCode(discountCode).then(function() {
            // Re-trigger click with flag to skip interception
            isApplyingDiscount = true;
            paymentButton.click();
            setTimeout(function() { isApplyingDiscount = false; }, 100);
          }).catch(function() {
            isApplyingDiscount = true;
            paymentButton.click();
            setTimeout(function() { isApplyingDiscount = false; }, 100);
          });
        }
      }
    }, true); // Use capture phase
  }

  /**
   * Track which discount code is currently applied
   */
  var currentAppliedDiscount = null;

  /**
   * Pre-apply discount code on page load for eligible first-time customers
   * On collection pages (multiple wrappers), sets up hover-based discount switching
   */
  function preApplyDiscount() {
    // Auto-discount application removed - discount applies via Shopify automatic discounts
    return;
    if (hasTrialHistory()) { // eslint-disable-line no-unreachable
      return;
    }

    var wrappers = document.querySelectorAll('[data-trial-wrapper]');

    // Single product page - just apply the discount
    if (wrappers.length === 1) {
      var handle = getProductHandle(wrappers[0]);
      var discountCode = getDiscountCode(handle);
      if (discountCode) {
        currentAppliedDiscount = discountCode;
        applyDiscountCode(discountCode);
      }
      return;
    }

    // Collection page (multiple products) - apply discount on hover
    // This ensures the correct discount is applied before user clicks Buy Now
    wrappers.forEach(function(wrapper) {
      wrapper.addEventListener('mouseenter', function() {
        if (hasTrialHistory()) return;

        var handle = getProductHandle(wrapper);
        var discountCode = getDiscountCode(handle);

        // Only apply if different from current
        if (discountCode && discountCode !== currentAppliedDiscount) {
          currentAppliedDiscount = discountCode;
          applyDiscountCode(discountCode);
        }
      });
    });
  }

  /**
   * Update page UI based on eligibility
   * Sets body class which CSS uses to show/hide elements
   */
  function updatePageUI() {
    var isFirstTime = !hasTrialHistory();
    var body = document.body;

    if (isFirstTime) {
      body.classList.add('trial-eligible');
      body.classList.remove('trial-ineligible');
    } else {
      body.classList.add('trial-ineligible');
      body.classList.remove('trial-eligible');
    }
    // CSS handles visibility of .trial-price--, .trial-discount-message,
    // and [data-trial-first-time] elements based on body class
  }

  /**
   * Mark trial as purchased (call on thank-you page)
   */
  function markTrialPurchased() {
    // Clear trial checkout flag (allows user to access /cart again)
    try {
      localStorage.removeItem('trialBox');
    } catch (e) {}

    return getFingerprint().then(function(fp) {
      setCookie();
      setLocalStorage(fp);
    });
  }

  /**
   * Check if current page has trial product
   */
  function isTrialProductPage() {
    return document.querySelector('[data-trial-add-to-cart]') !== null;
  }

  /**
   * Check if on thank-you page with trial product AND trial discount applied
   * Only marks as purchased if discount was actually used
   */
  function isThankYouWithTrialDiscount() {
    if (!window.Shopify || !window.Shopify.checkout) {
      return false;
    }

    var checkout = window.Shopify.checkout;

    // Check for trial product
    var lineItems = checkout.line_items || [];
    var hasTrialProduct = lineItems.some(function(item) {
      return item.product_type === 'Trial Box' ||
             (item.properties && item.properties._isTrial);
    });

    if (!hasTrialProduct) {
      return false;
    }

    // Check for trial discount code
    // Shopify checkout exposes applied_discount or discount_applications
    var discountCodes = [];
    if (checkout.discount && checkout.discount.code) {
      discountCodes.push(checkout.discount.code.toUpperCase());
    }
    if (checkout.applied_discount && checkout.applied_discount.code) {
      discountCodes.push(checkout.applied_discount.code.toUpperCase());
    }
    if (checkout.discount_applications) {
      checkout.discount_applications.forEach(function(d) {
        if (d.code) discountCodes.push(d.code.toUpperCase());
      });
    }

    var trialCodes = ['PUPPY-SAVE', 'NOVEL-PROTEIN-SAVE', 'LOWFAT-SAVE', 'PICKY-EATER-SAVE', 'BESTSELLERS-SAVE', 'CAT TRIAL - SAVE30'];
    var hasTrialDiscount = trialCodes.some(function(code) {
      return discountCodes.indexOf(code) !== -1;
    });

    return hasTrialDiscount;
  }

  /**
   * Check if on any thank-you page with a trial product (regardless of discount)
   */
  function isThankYouWithTrialProduct() {
    if (!window.Shopify || !window.Shopify.checkout) {
      return false;
    }
    var checkout = window.Shopify.checkout;
    var lineItems = checkout.line_items || [];
    return lineItems.some(function(item) {
      return item.product_type === 'Trial Box' ||
             (item.properties && item.properties._isTrial);
    });
  }

  /**
   * Initialize
   */
  function init() {
    // Global interceptor for Shopify payment buttons (handles Shadow DOM)
    setupGlobalPaymentButtonInterceptor();

    // Clear trial checkout flag on ANY trial purchase (allows /cart access again)
    if (isThankYouWithTrialProduct()) {
      try {
        localStorage.removeItem('trialBox');
      } catch (e) {}
    }

    // Handle thank-you page - only mark as purchased if discount was actually used
    if (isThankYouWithTrialDiscount()) {
      markTrialPurchased();
    }

    // Listen for trial box added from collection page (or any non-PDP context)
    document.addEventListener('ajaxProduct:added', function(e) {
      if (hasTrialHistory()) return; // Not eligible

      var detail = e.detail || {};
      var handle = detail.product && detail.product.handle;
      if (!handle) return;

      var discountCode = getDiscountCode(handle);
      if (discountCode) {
        applyDiscountCode(discountCode);
      }
    });

    var clientEligible = !hasTrialHistory();

    // UX: Default to showing regular price (CSS handles this)
    // Only add trial-eligible class AFTER server confirms eligibility
    // This prevents flash of discounted price for ineligible users

    if (!clientEligible) {
      // Client-side says ineligible - update UI immediately, no server check needed
      updatePageUI();
      // Still store fingerprint for tracking
      getFingerprint().then(function(fp) {
        return storeFingerprint(fp);
      }).catch(function() {
        // Non-blocking
      });
    } else {
      // Client-side says eligible - check if QA override should bypass server
      var qaOverride = getQAOverride();

      if (qaOverride === 'eligible') {
        // QA override: skip server check, force eligible
        updatePageUI();
        preApplyDiscount();
        getFingerprint().then(function(fp) {
          return storeFingerprint(fp);
        }).catch(function() {});
      } else {
        // Normal flow: wait for server confirmation before showing discount
        checkServerEligibility().then(function(result) {
          // NOW update UI based on server result
          updatePageUI();

          // Only pre-apply discount if still eligible after server check
          if (result.eligible) {
            preApplyDiscount();
          }

          // Store fingerprint in cart for webhook access (regardless of eligibility)
          return getFingerprint();
        }).then(function(fp) {
          return storeFingerprint(fp);
        }).catch(function() {
          // On error, default to eligible (webhook will catch abuse)
          updatePageUI();
          preApplyDiscount();
        });
      }
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for external use
  window.TrialDiscount = {
    hasTrialHistory: hasTrialHistory,
    markTrialPurchased: markTrialPurchased,
    getFingerprint: getFingerprint,
    getDiscountCode: getDiscountCode,
    checkServerEligibility: checkServerEligibility,
    isFirstTime: function() { return !hasTrialHistory(); },
    currency: currency
  };

})();
